import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";

/**
 * Acquitte l'exécution effective d'une demande APPROVED
 * (`POST /ApprovalRequests/{id}/mark-applied` → APPLIED). Rôles :
 * flashpay-superadmin, flashpay-service. Appelé par le front après succès de
 * l'action protégée (le service concerné acquitte aussi de son côté, §5.4).
 */
export const useMarkApprovalApplied = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "approval-requests", "mark-applied"],
    mutationFn: (approvalRequestId: string) =>
      mutateData({
        url: adminEndpoints.approvalRequests.markApplied(approvalRequestId),
        method: "POST",
      }) as Promise<ApprovalRequestDto>,
    onSuccess: (_data, approvalRequestId) => {
      return invalidateAfterMutation(
        queryClient,
        adminApprovalCacheKeys(approvalRequestId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useMarkApprovalApplied failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
